/**
 * A Vue.js component to render images within
 * a Vue.js application.
 *
 * @param {String} src The default image src
 * @param {String} srcset An option srcset value
 * @param {String} alt Alt text to represent the image
 * @param {String} className CSS classes to add to the parent
 * @param {String} fit "cover" or "contain"
 * @param {String} pos "center", "top" or "bottom"
 * @param {String} width Image width
 * @param {String} height Image height
 * @param {Boolean} lazy Should the image load in lazily
 * @param {Boolean} transition Should there be a fade in transition?
 *
 * @example
 * <div
 *   is="vue-image-v2"
 *   :src="imageSrc"
 *   :src-mobile="imageSrc"
 *   :alt="title"
 *   fit="cover"
 *   css="product-card__image"
 *   width="100px"
 *   height="200px" />
 */
export default {
  name: 'vue-image-v2',
  props: {
    src: {
      type: String,
      required: true
    },
    srcMobile: {
      type: String,
      default: ''
    },
    sizes: {
      type: String,
      required: false
    },
    alt: {
      type: String,
      required: false
    },
    fit: String,
    pos: String,
    width: String,
    height: String,
    fullWidth: {
      type: Boolean,
      default: false,
    },
    transition: {
      type: Boolean,
      default: false,
    },
    slidevalue: {
      type: String,
    },
  },
  data() {
    return {
      currentSrc: this.src || ""
    }
  },
  watch: {
    src(newSrc) {
      if (newSrc) {
        if (!this.srcMobile || window.innerWidth >= 768) {
          this.currentSrc = newSrc;
        }
      }
    },
    srcMobile(newSrcMobile) {
      if (newSrcMobile && newSrcMobile !== '' && window.innerWidth < 768) {
        this.currentSrc = newSrcMobile;
      }
    }
  },
  computed: {
    srcSet(){
      const breakpoints = [100, 200, 300, 400, 600, 800, 1000, 1440, 1920, 2048]
      return breakpoints.map(brk => {
        const srcString = this.currentSrc?.startsWith('//') ? window.location.protocol + this.currentSrc : this.currentSrc;
        const src = new URL(srcString);
        src.searchParams.set('width', brk.toString());
        return `${src} ${brk}w`;
      }).join(',')
    },
    computedStyles() {
      const styles = {
        objectFit: this.fit,
        objectPosition: this.pos,
        width: this.fullWidth ? '100%' : this.width,
        height: this.height
      };

      if (this.slidevalue) {
        styles.clipPath = `polygon(0 0, ${Number(this.slidevalue)}% 0, ${Number(this.slidevalue)}% 100%, 0 100%)`;
      }

      return styles;
    }
  },
  template: `
    <picture v-if="srcMobile" class="img">
      <source
        media="(max-width: 767px)"
        :srcset="srcMobile"
        :sizes="sizes"
      />
      <source
        media="(min-width: 768px)"
        :srcset="srcSet"
        :sizes="sizes"
      />
      <img
        :width="width"
        :height="height"
        class="img__el"
        :src="src"
        :alt="alt"
        :style="computedStyles"
      />
    </picture>
    <transition 
      v-else-if="transition" 
      name="fade" 
      mode="out-in" 
      appear
      :duration="{ enter: 200, leave: 200 }"
    > 
      <img
        :key="currentSrc"
        :width="width"
        :height="height"
        class="img__el transition"
        :src="currentSrc"
        :srcset="srcSet"
        :sizes="sizes"
        :alt="alt"
        :style="computedStyles"
      />
    </transition>
    <img
      v-else
      :width="width"
      :height="height"
      class="img__el"
      :src="src"
      :srcset="srcSet"
      :sizes="sizes"
      :alt="alt"
      :style="computedStyles"
    />
  `,
  style: `
    .fade-enter-active, .fade-leave-active {
      transition: opacity 0.3s ease;
    }
    .fade-enter-from, .fade-leave-to {
      opacity: 0;
    }
    .fade-enter-to, .fade-leave-from {
      opacity: 1;
    }
  `
};